
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TreeMapFilterable from '@/components/reports-v3/components/filterables/TreeMapFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import * as echarts from 'echarts';
import IncludeUnappliedToggle from '@/components/IncludeUnappliedToggle.vue';
import settingx from '@/store/modules/settingx';
import { getTableName } from '@/worker/db/sqlHelpers';
import gdbx from '@/store/modules/gdbx';

@Component({
	components: {
		TreeMapFilterable,
		IncludeUnappliedToggle,
	},
})
export default class SupplierOutstandingTreeMap extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['suppliers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['suppliers', 'agents', 'dateAsOf'];

	public result: any = [];
	public series: echarts.ECharts[] | any = [];

	public get dateFormatted() {
		return [this.selectedAsOfDate];
	}

	public get includeUnapplied() {
		return settingx.now.includeUnapplied;
	}

	public get expensiveHook() {
		const { selectedAsOfDate, selectedAgents, selectedSuppliers, includeUnapplied } = this;
		return JSON.stringify([
			selectedAsOfDate,
			selectedAgents,
			selectedSuppliers,
			includeUnapplied,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const queryHead = await this._ensureTablesLoaded(
			['supplierTrades'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		const tradesTable = getTableName('supplierTrades');
		const dateFilter = `TRY_CAST("DOCDATE" AS BIGINT) <= ${this.selectedAsOfDate}`;
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');

		const allAgentLen = (gdbx.allAgents && gdbx.allAgents.length) || 0;
		const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
			&& this.selectedAgents.length !== allAgentLen
			? ` AND "AGENT" IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
			: '';

		const allSupplierLen = (gdbx.allSuppliers && gdbx.allSuppliers.length) || 0;
		const supplierFilter = this.selectedSuppliers && this.selectedSuppliers.length > 0
			&& this.selectedSuppliers.length !== allSupplierLen
			? ` AND "CODE" IN (${this.selectedSuppliers.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';

		const baseWhere = `${dateFilter}${agentFilter}${supplierFilter}`;

		// Query OS amount for PI/SD docs
		const osQuery = queryHead.rawQuery(`
			SELECT "CODE" as code,
				SUM(TRY_CAST("LOCALOSAMOUNT" AS DOUBLE)) as osAmount
			FROM ${tradesTable}
			WHERE ${baseWhere}
				AND "DOCTYPE" IN ('PI', 'SD')
			GROUP BY "CODE"
		`);

		let minusUnappliedQuery: Promise<any[]> | null = null;
		let plusUnappliedQuery: Promise<any[]> | null = null;

		if (this.includeUnapplied) {
			// Minus unapplied for SC/ST/SI/SP
			minusUnappliedQuery = queryHead.rawQuery(`
				SELECT "CODE" as code,
					SUM(TRY_CAST("LOCALUNAPPLIEDAMT" AS DOUBLE)) as unappliedAmount
				FROM ${tradesTable}
				WHERE ${baseWhere}
					AND "DOCTYPE" IN ('SC', 'ST', 'SI', 'SP')
				GROUP BY "CODE"
			`);

			// Plus unapplied for SF
			plusUnappliedQuery = queryHead.rawQuery(`
				SELECT "CODE" as code,
					SUM(TRY_CAST("LOCALUNAPPLIEDAMT" AS DOUBLE)) as unappliedAmount
				FROM ${tradesTable}
				WHERE ${baseWhere}
					AND "DOCTYPE" IN ('SF')
				GROUP BY "CODE"
			`);
		}

		const queries: Array<Promise<any[]>> = [osQuery];
		if (minusUnappliedQuery) { queries.push(minusUnappliedQuery); }
		if (plusUnappliedQuery) { queries.push(plusUnappliedQuery); }

		const results = await Promise.all(queries);
		this.checkInterrupted(jobId);

		const osRows = results[0];
		const minusRows = this.includeUnapplied ? results[1] : [];
		const plusRows = this.includeUnapplied ? results[2] : [];

		// Build lookup maps for unapplied
		const minusMap = new Map<string, number>();
		for (const row of minusRows) {
			minusMap.set(row.code, Number(row.unappliedAmount) || 0);
		}
		const plusMap = new Map<string, number>();
		for (const row of plusRows) {
			plusMap.set(row.code, Number(row.unappliedAmount) || 0);
		}

		this.result = [];

		for (const row of osRows) {
			let value = Number(row.osAmount) || 0;
			if (this.includeUnapplied) {
				value = value - (minusMap.get(row.code) || 0) + (plusMap.get(row.code) || 0);
			}
			this.result.push({
				name: gdbx.supplierNames[row.code] || row.code,
				value,
			});
		}

		this.generateLoadingText(1);

		this.series = [];

		const stock = {
			name: 'Supplier Outstanding Amount',
			type: 'treemap',
			roam: 'move',
			width: '100%',
			drillDownIcon: '',
			leafDepth: 1,
			label: {
				color: '#000',
			},
			itemStyle: {
				gapWidth: 1,
				borderWidth: 1,
			},
			data: this.result,
		};

		this.series = [stock];

		this.saveHistory('result', 'series');
	}
}
